import {
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { HeadTable, TableCaption } from 'components';
import { getSortItem } from 'helpers';
import { useDateTime } from 'hooks';
import { DOCUMENT_TYPE, GET_DOCUMENT_PARAMS_REQUEST } from 'models';
import { memo, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSitterDocumentsParams } from 'store/reducers/accounts';
import { getSitterDocumentAction } from '../../../../../store/reducers/accounts/actionTypes';
import { headCells } from './data';

interface Props {
  sitter_id?: string;
}

const Documents = memo(({ sitter_id }: Props) => {
  const dispatch = useAppDispatch();
  const { formatDateTime } = useDateTime();
  const [selected, setSelected] = useState<readonly string[]>([]);

  const { documents } = useAppSelector(
    (state) => state.accountReducer.sitters.sitter_list_detail
  );

  const { params } = documents;

  const onSetSitterDocumentParams = (
    params: GET_DOCUMENT_PARAMS_REQUEST | null
  ) => {
    dispatch(setSitterDocumentsParams(params));
  };

  useEffect(() => {
    if (sitter_id) {
      dispatch(getSitterDocumentAction(sitter_id, params));
    }
  }, [params, sitter_id, dispatch]);

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetSitterDocumentParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: documents.metadata.total_pages,
    page: documents.metadata.page,
    onChange(_event, page) {
      onSetSitterDocumentParams({ ...params, page: page });
    },
  });

  const sortItem = useMemo(() => {
    return getSortItem(params?.sort);
  }, [params]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = documents?.records?.map((n: any) => n?.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleRequestSort = (_: any, property: keyof DOCUMENT_TYPE) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetSitterDocumentParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  return (
    <Stack spacing="8px">
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxHeight: 'calc(100vh - 355px)',
          border: '1px solid #D9D9D9',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }} aria-labelledby="tableTitle">
          <HeadTable<DOCUMENT_TYPE>
            numSelected={selected.length}
            order={sortItem?.order}
            orderBy={sortItem?.orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={documents?.records.length}
            onAction={false}
            onCheckbox={false}
            headCells={headCells}
          />
          <TableBody
            sx={{
              height: 'auto !important',
            }}
          >
            {documents.records.map((row, index) => {
              const isItemSelected = isSelected(row.id);
              const labelId = `enhanced-jobs-table-cb-${index}`;
              return (
                <TableRow
                  hover
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                >
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    sx={{
                      borderRight: '1px solid #DEE2E6',
                      padding: '18px 12px 17px',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      borderRight: '1px solid #DEE2E6',
                      padding: '18px 12px 17px',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {row.Sitter?.User?.full_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    width={600}
                    sx={{
                      borderRight: '1px solid #DEE2E6',
                      padding: '18px 12px 17px',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    <a href={row.s3_url}>Document</a>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      borderRight: '1px solid #DEE2E6',
                      padding: '18px 12px 17px',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {formatDateTime(row.createdAt)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TableCaption
        rowsPerPage={params?.limit ?? 0}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        metadata={documents?.metadata}
        items={items}
      />
    </Stack>
  );
});

export default Documents;
